// eslint-disable-next-line max-classes-per-file
import axios, { AxiosRequestConfig, AxiosResponse, CancelToken } from "axios";
import { BaseResponse } from "./types";
import type { components } from "./openapi";
import JSONbigint from "json-bigint";

// Without useNativeBigInt, json-bigint represents large integers as BigNumber
// (bignumber.js) instances instead of native bigint. IChat/message ids are typed
// as bigint and get passed straight into flatbuffers' 64-bit field writers (see
// chat-transport.ts), which do raw bigint arithmetic and throw "Cannot mix
// BigInt and other types" on a BigNumber — this must produce real bigints.
export const JSONbig = JSONbigint({ useNativeBigInt: true });
import { forgetStoredDevice, storedDeviceId } from "./device-id";
import Service, { authTokenKey, refreshTokenKey, type AuthResponse } from "./auth";
import type {
  Endpoint,
  EndpointWith,
  HttpMethod,
  Result,
  TypedOptions,
} from "./spec";
import { setItem, getItem, removeItem } from "./storage";
import * as jwtDecode from "jwt-decode";

let getConfigValue = (key: string): string | undefined => {
  console.warn(`Configuration for ${key} not provided`);
  return undefined;
};

export function initializeConfig(
  configImplementation: (key: string) => string | undefined
) {
  getConfigValue = configImplementation;
}

export interface IParams {
  [key: string]: string;
}

export interface IHeader {
  [key: string]: any;
}

export interface IBody {
  [key: string]: any;
}

export enum TokenType {
  Auth = 0,
  Ano = 1,
}

export interface IOption {
  body?: IBody;
  hasAuth?: boolean;
  headers?: IHeader;
  params?: IParams;
  cancelToken?: CancelToken;
  isLocal?: boolean;
  /** Milliseconds before axios gives up on the call. None by default. */
  timeout?: number;
}

export interface DefaultReq {
  page?: number;
  pageSize?: number;
}

export interface Cursor {
  cursor?: number | string | bigint;
  limit?: number;
}

export const getToken = () => getItem(authTokenKey);
export const getRefreshToken = () => getItem(refreshTokenKey);

type JwtPayload = {
  id: string;
  exp: number;
};

/**
 * A token this close to `exp` is refreshed now. Checking `exp` against the bare
 * clock let a token pass the check and expire on its way to the server, and a
 * client clock running a little slow did the same.
 */
const EXPIRY_LEEWAY_S = 30;

let onUnauthorized: (() => void) | undefined;
/**
 * Registers what happens when the session is over: the web app ends it
 * (`@stringf/store/session`), the admin console drops back to its sign-in.
 * Returns a function that puts the previous handler back, for effect cleanups.
 */
export function initializeLogout(handler: () => void): () => void {
  const previous = onUnauthorized;
  onUnauthorized = handler;
  return () => {
    if (onUnauthorized === handler) onUnauthorized = previous;
  };
}

/** The server has refused these credentials: forget them and end the session. */
const signOut = () => {
  removeItem(authTokenKey);
  removeItem(refreshTokenKey);
  onUnauthorized?.();
};

const sessionOver = (message: string) => {
  signOut();
  return new ApiErrorResponse(401, message);
};

const decode = (token: string): JwtPayload | undefined => {
  try {
    return jwtDecode.jwtDecode<JwtPayload>(token);
  } catch {
    return undefined;
  }
};

/**
 * Whether a failed refresh is the server refusing the refresh token, as opposed
 * to failing to answer. stringr refuses a dead or spent refresh token with HTTP
 * 400 and a full lockout with code 4010 (`sessions.rs`), never a 401, so a check
 * for 401 alone left the user holding dead tokens. A network error or a 5xx says
 * nothing about the token, so it is not a refusal.
 */
export const isRefreshRefusal = (err: unknown): boolean => {
  if (!(err instanceof ApiErrorResponse)) return false;
  if (err.httpStatus !== undefined && err.httpStatus >= 500) return false;
  if (err.httpStatus === 400 || err.httpStatus === 401 || err.httpStatus === 403) return true;
  return [400, 401, 403, 4010].includes(err.statusCode);
};

/**
 * A request the server refused on its merits (400/401/403). Asking again gets
 * the same answer, so React Query should not.
 */
export const isRefusedRequest = (err: unknown): boolean =>
  err instanceof ApiErrorResponse && [400, 401, 403].includes(err.httpStatus ?? err.statusCode);

/** Another tab rotated the refresh token while ours was out: its tokens are the live ones. */
const rotatedElsewhere = (spent: string): string | undefined => {
  const refresh = getRefreshToken();
  const token = getToken();
  return refresh && refresh !== spent && token ? token : undefined;
};

/** The account the stored access token belongs to; `undefined` when signed out. */
export const sessionAccountId = (): string | undefined => {
  const token = getToken();
  return token ? decode(token)?.id : undefined;
};

let onDeviceUnknown: ((accountId: string) => void) | undefined;
/**
 * What a refresh does when stringr says the Device it named is gone: the Device
 * module registers itself here (it imports this one, so this one cannot import it).
 */
export const setDeviceUnknownHandler = (handler: (accountId: string) => void) => {
  onDeviceUnknown = handler;
};

/*
  One refresh at a time, shared by every caller that needs it. Refreshing spends
  the refresh token, so a second concurrent refresh would present a token the
  first has already retired and be refused. Every waiter settles with the one
  refresh: it used to be a subscriber list that a failure emptied without calling,
  which left those requests hanging forever.
*/
let refreshing: Promise<string> | null = null;

const refresh = (accountId: string): Promise<string> =>
  (refreshing ??= rotate(accountId).finally(() => {
    refreshing = null;
  }));

const rotate = async (accountId: string): Promise<string> => {
  const spent = getRefreshToken();
  if (!spent) throw sessionOver("token expired");
  let res: AuthResponse;
  const named = storedDeviceId(accountId);
  try {
    res = await Service.refreshToken(accountId, spent, undefined, named);
  } catch (err) {
    const theirs = rotatedElsewhere(spent);
    if (theirs) return theirs;
    if (isRefreshRefusal(err)) throw sessionOver("Failed to refresh token");
    // Transient: the tokens may still be good, so they stay and the call fails.
    throw err;
  }
  if (getRefreshToken() !== spent) {
    // Signed out, or another tab rotated, while this refresh was out. Storing
    // these would bring a session back that has ended or been replaced.
    const theirs = rotatedElsewhere(spent);
    if (theirs) return theirs;
    throw new ApiErrorResponse(401, "signed out during refresh");
  }
  const accessToken = res.jwtToken ?? res.token;
  if (!accessToken || !res.refreshToken) throw new Error(GENERIC_ERROR_MESSAGE);
  setItem(authTokenKey, accessToken);
  setItem(refreshTokenKey, res.refreshToken);
  // Once per refresh: the registration does not name a Device, so it cannot flag
  // itself unknown. A tab that already replaced the id is left alone.
  if (res.deviceUnknown && named && storedDeviceId(accountId) === named) {
    forgetStoredDevice();
    onDeviceUnknown?.(accountId);
  }
  return accessToken;
};

/**
 * The bearer token to send, refreshed first when it is expired or about to be.
 * No token, or one that does not decode, is a session that cannot continue.
 */
const refreshIfExpired = async (): Promise<string> => {
  const token = getToken();
  if (!token) throw sessionOver("does not have token");
  const claims = decode(token);
  if (!claims?.exp || !claims.id) throw sessionOver("invalid token");
  if (claims.exp - EXPIRY_LEEWAY_S > Math.floor(Date.now() / 1000)) return token;
  return refresh(claims.id);
};

/**
 * After the server refused `rejected` (a 401 on a bearer call): a fresh token.
 * If another call already replaced it, that one, without refreshing again.
 */
const refreshAfterRejection = async (rejected: string): Promise<string> => {
  const token = getToken();
  if (!token) throw sessionOver("does not have token");
  if (token !== rejected) return token;
  const claims = decode(token);
  if (!claims?.id) throw sessionOver("invalid token");
  return refresh(claims.id);
};

export const getTokenAndRefresh = (): Promise<string> => refreshIfExpired();

/**
 * Revokes a session on the server after the client has already forgotten it, so
 * nothing here reads or writes storage. An expired access token is refreshed
 * first, and the logout then carries the refresh token that refresh issued: the
 * one it presented is spent, and revoking it would leave the new session live.
 * Best-effort; it never rejects.
 */
export const revokeSession = async (
  session: { token?: string | null; refresh: string; accountId?: string; deviceId?: string },
  timeoutMs = 5_000
): Promise<void> => {
  try {
    const claims = session.token ? decode(session.token) : undefined;
    let token = session.token ?? undefined;
    let refreshToken = session.refresh;
    if (!claims?.exp || claims.exp - EXPIRY_LEEWAY_S <= Math.floor(Date.now() / 1000)) {
      const accountId = claims?.id ?? session.accountId;
      if (!accountId) return;
      const res = await Service.refreshToken(accountId, session.refresh, timeoutMs);
      token = res.jwtToken ?? res.token;
      refreshToken = res.refreshToken;
    }
    if (!token) return;
    await Service.logout(
      session.deviceId ? { refreshToken, deviceId: session.deviceId } : { refreshToken },
      token,
      timeoutMs
    );
  } catch {
    // The session is over on this device either way.
  }
};

const genHeader = (token?: string, headers = {}) =>
  Object.assign(
    token ? { Authorization: `Bearer ${token}` } : {},
    { "Content-Type": "application/json" },
    headers
  );

/**
 * The REST base URL this build talks to, e.g. `https://api.astring.app/api/v1/`.
 *
 * Exported for the media layer, which builds public `media/file/<key>` URLs off
 * the same origin the API is on — the way every client does (`MediaRef` on iOS).
 * Reading it from the same config the client uses keeps the two from drifting,
 * which a separate storage-URL environment variable has already done.
 */
export const apiBaseUrl = (): string | undefined => getConfigValue("BASE_API_URL");

export const NETWORK_ERROR = "network_error";

export const GENERIC_ERROR_MESSAGE = "request failed";

/**
 * A non-2xx reply carries the backend's envelope (`{code, message, ...}`, with the
 * business code in `code`, stringr ADR-0004 D-B). `transformResponse` is identity,
 * so `data` is the raw string. Only a missing response is a transport failure;
 * an unparsable body (proxy error page) keeps the HTTP status.
 */
const handleError = (err: any, reject: any) => {
  console.error("server err:", err);
  const response = err?.response;
  if (!response) {
    reject(new Error(NETWORK_ERROR));
    return;
  }
  const status: number = response.status;
  let body: { code?: unknown; message?: unknown; errors?: unknown; result?: unknown } | undefined;
  try {
    const parsed =
      typeof response.data === "string" ? JSONbig.parse(response.data) : response.data;
    if (parsed && typeof parsed === "object") body = parsed;
  } catch {
    // not JSON: fall through to the generic message
  }
  const code = typeof body?.code === "number" ? body.code : status;
  const message =
    typeof body?.message === "string" && body.message ? body.message : GENERIC_ERROR_MESSAGE;
  const refused = new ApiErrorResponse(code, message, status, readFieldErrors(body?.errors));
  refused.result = body?.result;
  reject(refused);
};

/** One refused field of a validation 400 (`api.FieldError`); the client words it, never the server. */
export type FieldError = components["schemas"]["api.FieldError"];

/** The `errors` list of a validation 400, minus entries that are not `{field, rule}`; `undefined` when none. */
const readFieldErrors = (raw: unknown): FieldError[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const ok = raw.filter(
    (e): e is FieldError =>
      !!e && typeof e.field === "string" && typeof e.rule === "string"
  );
  return ok.length ? ok : undefined;
};

export class ApiErrorResponse extends Error {
  /** The envelope's business code when the body carries one, else the HTTP status. */
  public statusCode;
  /** The HTTP status of the reply, kept because `statusCode` hides it behind the envelope code. */
  public httpStatus?: number;
  /** Which fields a validation 400 refused and why (`errors` in the body), when it carries them. */
  public fieldErrors?: FieldError[];
  /** The body's `result` on a refusal that carries one (a 409 names the group's epoch and the Devices to remove). */
  public result?: unknown;

  constructor(statusCode: number, message: string, httpStatus?: number, fieldErrors?: FieldError[]) {
    super(message);
    this.statusCode = statusCode;
    this.httpStatus = httpStatus;
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Drops properties whose value is `null`, recursively, so the runtime matches
 * the `NoNull` narrowing in `spec.ts`. Every response null is dropped by design:
 * the backend's `Option` fields mean "absent" and no client code tells null from
 * undefined. Applied once, to every successful response. Array elements and the
 * top-level value are left alone; only object properties are touched. Returns
 * the input untouched when it holds no null property.
 */
export const dropNulls = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    let out: unknown[] | undefined;
    value.forEach((el, i) => {
      const next = dropNulls(el);
      if (next !== el) (out ??= value.slice())[i] = next;
    });
    return out ?? value;
  }
  if (value === null || typeof value !== "object") return value;
  let out: Record<string, unknown> | undefined;
  for (const [k, v] of Object.entries(value)) {
    if (v === null) {
      out ??= { ...value };
      delete out[k];
      continue;
    }
    const next = dropNulls(v);
    if (next !== v) (out ??= { ...value })[k] = next;
  }
  return out ?? value;
};

const handleResponse = (
  resp: AxiosResponse<BaseResponse<any>, any>,
  resolve: any,
  reject: any
) => {
  if (resp.data.code === 200) {
    resolve({ ...resp.data, result: dropNulls(resp.data.result) });
  } else {
    reject(new ApiErrorResponse(resp.data.code, resp.data.message, resp.status));
  }
};

axios.interceptors.request.use((request) => {
  request.transformResponse = [(data) => data];
  if (request.data instanceof FormData) {
    return request;
  }

  request.data = JSONbig.stringify(request.data);
  return request;
});

// response interceptor parsing the response data with JSONbigint, and returning the response
axios.interceptors.response.use((response) => {
  response.data = JSONbig.parse(response.data);
  return response;
});

const request = async <T>(options: AxiosRequestConfig, isLocal = false) => {
  return new Promise<BaseResponse<T>>((resolve, reject) => {
    axios
      .request<BaseResponse<T>>({
        baseURL: isLocal
          ? getConfigValue("BASE_LOCAL_API_URL")
          : getConfigValue("BASE_API_URL"),
        ...options,
      })
      .then((resp) => {
        handleResponse(resp, resolve, reject);
      })
      .catch((err) => handleError(err, reject));
  });
};

/** A 401 on a call that carried our bearer token: the server refused the token. */
const isBearerRejection = (err: unknown) =>
  err instanceof ApiErrorResponse && (err.statusCode === 401 || err.httpStatus === 401);

/**
 * Sends one call. With `hasAuth`, a 401 is first taken as a stale token: one
 * refresh, one retry. The session ends only when the refresh is refused, or when
 * the server refuses the token it has just issued. A 401 on a call without our
 * bearer (a wrong passkey assertion, "invalid credentials") ends nothing.
 */
const send = async <T>(config: AxiosRequestConfig, options?: IOption): Promise<T> => {
  const attempt = (token?: string) =>
    request<T>(
      {
        ...config,
        headers: genHeader(token, options?.headers) as any,
        cancelToken: options?.cancelToken,
        timeout: options?.timeout,
      },
      options?.isLocal || false
    ).then((data) => data.result);

  if (!options?.hasAuth) return attempt();
  const token = await refreshIfExpired();
  try {
    return await attempt(token);
  } catch (err) {
    if (!isBearerRejection(err)) throw err;
  }
  const fresh = await refreshAfterRejection(token);
  try {
    return await attempt(fresh);
  } catch (err) {
    if (isBearerRejection(err)) signOut();
    throw err;
  }
};

const http = {
  get: <T>(url: string, options?: IOption): Promise<T> =>
    send<T>({ method: "GET", url, params: options?.params }, options),
  post: <T>(url: string, options?: IOption): Promise<T> =>
    send<T>({ method: "POST", url, data: options?.body }, options),
  put: <T>(url: string, options?: IOption): Promise<T> =>
    send<T>({ method: "PUT", url, data: options?.body }, options),
  del: <T>(url: string, options?: IOption): Promise<T> =>
    send<T>({ method: "DELETE", url, data: options?.body }, options),
  cancelToken: axios.CancelToken.source(),
};

export default http;

// ─── Spec-driven client ───────────────────────────────────────────────────────
// `api` is `http` with the endpoint, path params, request body and response type
// all derived from the OpenAPI spec. Prefer it over `http` in service modules:
// a URL, body field, or response shape the backend does not declare becomes a
// compile error instead of a runtime 404 or a silently-ignored field.

/** Substitutes `{param}` segments. Throws rather than issuing a malformed URL. */
export const fillPath = (
  url: string,
  path?: Record<string, string | number | bigint>
): string =>
  url.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = path?.[key];
    if (value === undefined || value === null || value === "") {
      throw new Error(`missing path param "${key}" for "${url}"`);
    }
    return encodeURIComponent(String(value));
  });

/**
 * Escape hatch: checks that `u` is a real endpoint but leaves body/response
 * typing to the caller.
 *
 * Needed where the generated types are wrong *for JavaScript* rather than wrong
 * about the backend — chiefly Go `int64` fields, which swaggo emits as
 * `integer` (TS `number`) but which must stay `bigint` here: message/bucket ids
 * exceed 2^53 and are parsed by json-bigint before going into flatbuffers'
 * 64-bit writers (see the JSONbig note at the top of this file).
 *
 * Prefer `api.*`. Every use of this is a spot where the spec should eventually
 * declare `type: string, format: int64`.
 */
export const specUrl = <U extends Endpoint>(url: U): U => url;

type CallOptions<U extends Endpoint, M extends HttpMethod> = TypedOptions<U, M> &
  Omit<IOption, "body">;

const call = <U extends Endpoint, M extends HttpMethod>(
  method: M,
  url: U,
  options: CallOptions<U, M>
): Promise<Result<U, M>> => {
  const { path, body, ...rest } = options as TypedOptions<U, M> &
    IOption & { path?: Record<string, string | number | bigint> };
  const resolved = fillPath(url, path);
  const opts: IOption = { ...rest, body: body as IBody | undefined };
  type R = Result<U, M>;
  switch (method) {
    case "get":
      return http.get<R>(resolved, opts);
    case "post":
      return http.post<R>(resolved, opts);
    case "put":
      return http.put<R>(resolved, opts);
    default:
      return http.del<R>(resolved, opts);
  }
};

export const api = {
  get: <U extends EndpointWith<"get">>(url: U, options: CallOptions<U, "get">) =>
    call("get", url, options),
  post: <U extends EndpointWith<"post">>(url: U, options: CallOptions<U, "post">) =>
    call("post", url, options),
  put: <U extends EndpointWith<"put">>(url: U, options: CallOptions<U, "put">) =>
    call("put", url, options),
  del: <U extends EndpointWith<"delete">>(url: U, options: CallOptions<U, "delete">) =>
    call("delete", url, options),
};

export let isMobile = false;

try {
  isMobile =
    typeof navigator !== "undefined" && navigator.product === "ReactNative";
} catch {
  isMobile = false;
}
